import { DownOutlined } from "@ant-design/icons";
import { Button, ColorPicker, Popover } from "antd";
import type { ColorPickerProps } from "antd";
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode
} from "react";
import { ErrorMessages } from "../../constants";
import { DEFAULT_CONDITIONAL_FORMATTING_ACTIONS } from "./normalize-rules";
import type { ConditionalFormattingActions } from "./types";

const cloneActions = (
  actions: ConditionalFormattingActions
): ConditionalFormattingActions => ({ ...actions });

const colorsMatch = (left?: string, right?: string) =>
  String(left ?? "").toLowerCase() === String(right ?? "").toLowerCase();

const normalizeColor = (color?: string) =>
  String(color ?? "")
    .trim()
    .toLowerCase();

const isCssVariableOrKeyword = (color?: string) => {
  const value = normalizeColor(color);
  return !value || value.startsWith("var(") || value === "transparent";
};

const isDefaultTextColor = (color?: string) => {
  const value = normalizeColor(color);
  return (
    isCssVariableOrKeyword(color) ||
    value === "#262626" ||
    value === "#000000" ||
    value === "black"
  );
};

const isDefaultFillColor = (color?: string) => {
  const value = normalizeColor(color);
  return (
    isCssVariableOrKeyword(color) ||
    value === "#ffffff" ||
    value === "#fff" ||
    value === "white"
  );
};

const toHexColor = (color: string | undefined, fallback: string) => {
  if (!color || isCssVariableOrKeyword(color)) return fallback;
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`.toUpperCase();
  }
  if (/^#[0-9a-f]{6}$/i.test(color)) return color.toUpperCase();
  return fallback;
};

/**
 * AG Grid theme vars are not defined outside the grid (e.g. in this popover).
 * Use these solids only for swatch preview; selection still stores the CSS vars.
 */
const DEFAULT_TEXT_SWATCH_PREVIEW = "#262626";
const DEFAULT_FILL_SWATCH_PREVIEW = "#FFFFFF";

/** Palette from Formatting Style design (after the default diagonal swatch). */
const STYLE_COLOR_PALETTE = [
  "#000000",
  "#FFFFFF",
  "#DD3D2B",
  "#B02417",
  "#A2D450",
  "#51AC5C",
  "#FABE55",
  "#FFFF53",
  "#4FADEA",
  "#2C70B9",
  "#091F5B",
  "#683395"
] as const;

const TEXT_STYLE_OPTIONS = [
  { key: "bold", label: "B", className: "font-bold" },
  { key: "italic", label: "I", className: "italic" },
  { key: "underline", label: "U", className: "underline" },
  { key: "strike", label: "S", className: "line-through" }
] as const;

type ColorTarget = "text" | "fill";

const COLOR_TARGET_ACTION_KEY: Record<
  ColorTarget,
  "textColor" | "backgroundColor"
> = {
  text: "textColor",
  fill: "backgroundColor"
};

const COLOR_TARGET_LABEL: Record<ColorTarget, string> = {
  text: "Text Colour",
  fill: "Fill Colour"
};

const COLOR_TARGET_CUSTOM_TITLE: Record<ColorTarget, string> = {
  text: "Custom Text Colour",
  fill: "Custom Fill Colour"
};

const COLOR_TARGET_CUSTOM_FALLBACK: Record<ColorTarget, string> = {
  text: "#262626",
  fill: "#FFFFFF"
};

function triggerButtonStyle(
  actions: ConditionalFormattingActions
): CSSProperties {
  return {
    color: actions.textColor,
    backgroundColor: actions.backgroundColor,
    fontWeight: actions.bold ? "bold" : undefined,
    fontStyle: actions.italic ? "italic" : undefined,
    textDecoration: [
      actions.underline ? "underline" : "",
      actions.strike ? "line-through" : ""
    ]
      .filter(Boolean)
      .join(" ")
  };
}

const EMPTY_ACTIONS: ConditionalFormattingActions = {
  ...DEFAULT_CONDITIONAL_FORMATTING_ACTIONS,
  bold: false,
  italic: false,
  underline: false,
  strike: false
};

function StylePreview({
  actions,
  className = "",
  children = "Sample Text"
}: {
  actions: ConditionalFormattingActions;
  className?: string;
  children?: ReactNode;
}) {
  const decorations = [
    actions.underline ? "underline" : "",
    actions.strike ? "line-through" : ""
  ].filter(Boolean);
  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      style={{
        color: actions.textColor,
        backgroundColor: actions.backgroundColor,
        fontWeight: actions.bold ? "bold" : undefined,
        fontStyle: actions.italic ? "italic" : undefined,
        textDecoration: decorations.join(" ") || undefined
      }}
    >
      {children}
    </span>
  );
}

function DefaultColorSwatch({
  selected,
  backgroundColor,
  showDiagonal,
  onClick,
  "aria-label": ariaLabel
}: {
  selected: boolean;
  backgroundColor: string;
  showDiagonal: boolean;
  onClick: () => void;
  "aria-label": string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={`relative h-5 w-5 overflow-hidden rounded-sm border border-solid p-0 ${
        selected ? "border-ciq-primary-900" : "border-ciq-gray-300"
      }`}
      style={{ backgroundColor }}
      onClick={onClick}
    >
      {showDiagonal ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 h-0.5 w-[141%] -translate-x-1/2 -translate-y-1/2 rotate-[-45deg] bg-ciq-danger"
        />
      ) : null}
    </button>
  );
}

function HueWheelSwatch({
  selected,
  onClick
}: {
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="More colours"
      aria-pressed={selected}
      className={`ciq-hue-wheel-swatch h-5 w-5 overflow-hidden rounded-sm border border-solid p-0 cursor-pointer ${
        selected ? "border-ciq-primary-900" : "border-ciq-gray-100"
      }`}
      onClick={onClick}
    />
  );
}

function PaintBucketIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden
      className={className}
      fill="currentColor"
    >
      <path d="M13.3 7.2 6.6.5 5.7 1.4l1.6 1.6L3.2 7.2l5.1 5.1 5-5.1ZM4.1 7.2 7.3 4l3.2 3.2H4.1Zm5.1 5.8s-1.3 1.4-1.3 2.3a1.3 1.3 0 1 0 2.6 0c0-.9-1.3-2.3-1.3-2.3Z" />
    </svg>
  );
}

function ColorTargetToggle({
  colorTarget,
  draftActions,
  onSelect
}: Readonly<{
  colorTarget: ColorTarget;
  draftActions: ConditionalFormattingActions;
  onSelect: (target: ColorTarget) => void;
}>) {
  return (
    <div className="mb-3 flex w-fit rounded-md border border-solid border-ciq-gray-300">
      <button
        type="button"
        aria-label="Text colour"
        aria-pressed={colorTarget === "text"}
        className={`flex h-8 items-center gap-1.5 rounded-l-md border-0 border-r border-solid border-ciq-gray-300 px-2.5 ${
          colorTarget === "text"
            ? "z-10 bg-ciq-blue-50 ring-1 ring-inset ring-ciq-primary-900"
            : "bg-white"
        }`}
        onClick={() => onSelect("text")}
      >
        <span className="relative text-sm font-medium text-black text-opacity-90">
          A
          <span
            className="absolute bottom-0 left-0 right-0 h-0.5 rounded-sm"
            style={{ backgroundColor: draftActions.textColor }}
          />
        </span>
        <DownOutlined
          className={`text-[10px] transition-transform ${
            colorTarget === "text"
              ? "rotate-180 text-ciq-primary-900"
              : "text-black text-opacity-45"
          }`}
        />
      </button>
      <button
        type="button"
        aria-label="Fill colour"
        aria-pressed={colorTarget === "fill"}
        className={`flex h-8 items-center gap-1.5 rounded-r-md border-0 px-2.5 ${
          colorTarget === "fill"
            ? "z-10 bg-ciq-blue-50 ring-1 ring-inset ring-ciq-primary-900"
            : "bg-white"
        }`}
        onClick={() => onSelect("fill")}
      >
        <span className="relative text-black text-opacity-90">
          <PaintBucketIcon />
          <span
            className="absolute -bottom-0.5 left-0 right-0 h-0.5 rounded-sm"
            style={{
              backgroundColor: isDefaultFillColor(draftActions.backgroundColor)
                ? "var(--ciq-border-subtle)"
                : draftActions.backgroundColor
            }}
          />
        </span>
        <DownOutlined
          className={`text-[10px] text-black text-opacity-45 transition-transform ${
            colorTarget === "fill" ? "rotate-180 text-ciq-primary-900" : ""
          }`}
        />
      </button>
    </div>
  );
}

function SwatchGrid({
  colorTarget,
  activeSwatches,
  isDefaultSelected,
  isCustomSelected,
  isSwatchSelected,
  onSelectDefault,
  onSelectSwatch,
  onOpenCustom
}: Readonly<{
  colorTarget: ColorTarget;
  activeSwatches: ReadonlyArray<string>;
  isDefaultSelected: boolean;
  isCustomSelected: boolean;
  isSwatchSelected: (swatch: string) => boolean;
  onSelectDefault: () => void;
  onSelectSwatch: (swatch: string) => void;
  onOpenCustom: () => void;
}>) {
  return (
    <div className="mb-4 grid grid-cols-7 gap-2">
      <DefaultColorSwatch
        selected={isDefaultSelected}
        backgroundColor={
          colorTarget === "text"
            ? DEFAULT_TEXT_SWATCH_PREVIEW
            : DEFAULT_FILL_SWATCH_PREVIEW
        }
        showDiagonal={colorTarget === "fill"}
        aria-label={`Select default ${colorTarget} colour`}
        onClick={onSelectDefault}
      />
      {activeSwatches.map((swatch) => {
        const selected = isSwatchSelected(swatch);
        const isWhiteSwatch = normalizeColor(swatch) === "#ffffff";
        return (
          <button
            key={`${colorTarget}-${swatch}`}
            type="button"
            aria-label={`Select ${colorTarget} colour ${swatch}`}
            aria-pressed={selected}
            className={`h-5 w-5 rounded-sm border border-solid p-0 ${
              selected
                ? "border-ciq-primary-900"
                : isWhiteSwatch
                  ? "border-ciq-gray-300"
                  : "border-ciq-gray-100"
            }`}
            style={{ backgroundColor: swatch }}
            onClick={() => onSelectSwatch(swatch)}
          />
        );
      })}
      <HueWheelSwatch selected={isCustomSelected} onClick={onOpenCustom} />
    </div>
  );
}

type Props = {
  value: ConditionalFormattingActions;
  onChange: (actions: ConditionalFormattingActions) => void;
  disabled?: boolean;
};

export default function ConditionalFormattingStylePicker({
  value,
  onChange,
  disabled = false
}: Props) {
  const [open, setOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorTarget, setColorTarget] = useState<ColorTarget>("text");
  const [draftActions, setDraftActions] =
    useState<ConditionalFormattingActions>(() => cloneActions(value));
  const [customColor, setCustomColor] = useState("#1677FF");

  useEffect(() => {
    if (!open) {
      setDraftActions(cloneActions(value));
      setColorPickerOpen(false);
      setColorTarget("text");
    }
  }, [open, value]);

  const setDraftAction = (updates: Partial<ConditionalFormattingActions>) =>
    setDraftActions((current) => ({ ...current, ...updates }));

  // Omit the solid that matches AG Grid default so the diagonal swatch is unique.
  const textSwatches = useMemo(
    () =>
      STYLE_COLOR_PALETTE.filter(
        (color) => normalizeColor(color) !== "#000000"
      ),
    []
  );

  const fillSwatches = useMemo(
    () =>
      STYLE_COLOR_PALETTE.filter(
        (color) => normalizeColor(color) !== "#ffffff"
      ),
    []
  );

  const activeSwatches = colorTarget === "text" ? textSwatches : fillSwatches;
  const activeColor = draftActions[COLOR_TARGET_ACTION_KEY[colorTarget]];

  const isDefaultSelected =
    colorTarget === "text"
      ? isDefaultTextColor(draftActions.textColor)
      : isDefaultFillColor(draftActions.backgroundColor);

  const isSwatchSelected = (swatch: string) =>
    !isDefaultSelected && colorsMatch(swatch, activeColor);

  const isCustomSelected =
    !isDefaultSelected &&
    !activeSwatches.some((swatch) => colorsMatch(swatch, activeColor));

  const selectDefaultColor = () => {
    const actionKey = COLOR_TARGET_ACTION_KEY[colorTarget];
    setDraftAction({
      [actionKey]: DEFAULT_CONDITIONAL_FORMATTING_ACTIONS[actionKey]
    });
  };

  const openCustomColorPicker = (target: ColorTarget = colorTarget) => {
    const color = draftActions[COLOR_TARGET_ACTION_KEY[target]];
    setColorTarget(target);
    setCustomColor(toHexColor(color, COLOR_TARGET_CUSTOM_FALLBACK[target]));
    setColorPickerOpen(true);
  };

  const applyCustomColor = () => {
    setDraftAction({ [COLOR_TARGET_ACTION_KEY[colorTarget]]: customColor });
    setColorPickerOpen(false);
  };

  const selectSwatch = (swatch: string) => {
    setDraftAction({ [COLOR_TARGET_ACTION_KEY[colorTarget]]: swatch });
  };

  const customViewTitle = COLOR_TARGET_CUSTOM_TITLE[colorTarget];

  const customPanelRender: ColorPickerProps["panelRender"] = (panel) => (
    <div className="flex w-full flex-col">
      <div className="mb-2 font-semibold text-black text-opacity-90">
        {customViewTitle}
      </div>
      <div className="mb-1 text-xs text-black text-opacity-45">Preview</div>
      <StylePreview
        actions={{
          ...draftActions,
          [COLOR_TARGET_ACTION_KEY[colorTarget]]: customColor
        }}
        className="mb-4 h-8 w-full rounded-md border border-solid border-ciq-gray-300 text-sm"
      />

      <div className="mb-4 mx-auto">{panel}</div>

      <div className="flex justify-end gap-2">
        <Button onClick={() => setColorPickerOpen(false)}>Back</Button>
        <Button type="primary" onClick={applyCustomColor}>
          Select
        </Button>
      </div>
    </div>
  );

  const styleView = (
    <>
      <div className="mb-3 font-semibold text-black text-opacity-90">
        Formatting Style
      </div>

      <div className="mb-1 text-xs text-black text-opacity-45">Preview</div>
      <StylePreview
        actions={draftActions}
        className="mb-4 h-8 w-full rounded-md border border-solid border-ciq-gray-300 text-sm"
      />

      <div className="mb-1 text-xs text-black text-opacity-45">Text style</div>
      <div className="mb-4 flex gap-2">
        {TEXT_STYLE_OPTIONS.map(({ key, label, className }) => {
          const selected = !!draftActions[key];
          return (
            <button
              key={key}
              type="button"
              aria-label={`Toggle ${key}`}
              aria-pressed={selected}
              className={`flex h-8 w-8 items-center justify-center rounded-md border border-solid bg-white text-sm text-black text-opacity-90 ${className} ${
                selected
                  ? "!border-ciq-primary-900 !bg-ciq-blue-50 !text-ciq-primary-900"
                  : "border-ciq-gray-300"
              }`}
              onClick={() => setDraftAction({ [key]: !draftActions[key] })}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mb-1 text-xs text-black text-opacity-45">
        {COLOR_TARGET_LABEL[colorTarget]}
      </div>
      <ColorTargetToggle
        colorTarget={colorTarget}
        draftActions={draftActions}
        onSelect={setColorTarget}
      />

      <SwatchGrid
        colorTarget={colorTarget}
        activeSwatches={activeSwatches}
        isDefaultSelected={isDefaultSelected}
        isCustomSelected={isCustomSelected}
        isSwatchSelected={isSwatchSelected}
        onSelectDefault={selectDefaultColor}
        onSelectSwatch={selectSwatch}
        onOpenCustom={() => openCustomColorPicker()}
      />

      <div className="flex items-center justify-between gap-2">
        <Button
          type="link"
          danger
          className="!px-0"
          onClick={() => setDraftActions(cloneActions(EMPTY_ACTIONS))}
        >
          Clear All
        </Button>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setDraftActions(cloneActions(value));
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => {
              onChange(cloneActions(draftActions));
              setOpen(false);
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </>
  );

  const pickerContent = (
    <div className="relative w-[263px] p-1">
      <div
        aria-hidden={colorPickerOpen}
        className={colorPickerOpen ? "hidden" : undefined}
      >
        {styleView}
      </div>
      {colorPickerOpen ? (
        <div className="bg-white [&_.ant-color-picker-trigger]:hidden [&_.ant-popover]:!static [&_.ant-popover]:!inset-auto [&_.ant-popover]:!w-full [&_.ant-popover]:!max-w-none [&_.ant-popover]:!shadow-none [&_.ant-popover]:!transform-none">
          <ColorPicker
            value={customColor}
            open
            arrow={false}
            disabledAlpha
            defaultFormat="hex"
            placement="bottom"
            panelRender={customPanelRender}
            getPopupContainer={(node) => node.parentElement ?? document.body}
            styles={{
              popup: {
                root: {
                  position: "static",
                  inset: "auto",
                  width: "100%",
                  boxShadow: "none",
                  padding: 0,
                  transform: "none"
                }
              },
              popupOverlayInner: {
                width: "100%",
                padding: 0,
                boxShadow: "none"
              }
            }}
            onChange={(color) =>
              setCustomColor(color.toHexString().toUpperCase())
            }
            onOpenChange={() => undefined}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="right"
      autoAdjustOverflow
      getPopupContainer={() => document.body}
      zIndex={1100}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && colorPickerOpen) {
          setColorPickerOpen(false);
        }
        setOpen(nextOpen);
      }}
      content={pickerContent}
      classNames={{ root: "conditional-formatting-style-picker" }}
    >
      <button
        type="button"
        aria-label="Formatting style"
        aria-expanded={open}
        disabled={disabled}
        className="flex h-8 min-w-16 items-center justify-between gap-2 rounded border border-solid border-ciq-gray-400 px-3"
        style={triggerButtonStyle(value)}
        title={disabled ? ErrorMessages.PermissionNotGranted : ""}
      >
        <span className="min-w-6 text-sm">Aa</span>
        <DownOutlined className="text-xs text-black text-opacity-90 hover:text-black hover:text-opacity-90" />
      </button>
    </Popover>
  );
}
